/**
 * Schema for the "Credit Tele Verification Report" — the credit team's structured
 * TVR rendered as a single multi-column form.
 *
 * Persistence (see tvr-service.js): every field carries a `path` into the TVR API
 * object (`/case/:id/tvr-details/`) and a subsection is committed with one PATCH.
 *
 * Field shape:
 *   { key, label, type, path, options?, columns?, min?, max?, suffix?, wide? }
 *   key     — stable identifier; the audit-trail key and React key.
 *   path    — dotted path into the TVR API object.
 *   label   — human label shown above the control.
 *   type    — "text" (single-line input, packs into the column grid),
 *             "textarea" (multi-line, spans the full form width),
 *             "select" (dropdown; requires `options`),
 *             "date" (day picker; persists as YYYY-MM-DD),
 *             "number" (numeric; honours `min` / `max` / `suffix`),
 *             "currency" (numeric rupees; stored raw, displayed grouped),
 *             "boolean" (yes/no toggle),
 *             "list" (repeating group; requires `columns`).
 *   options — array of string options for `select` fields.
 *   columns — item-shape config for `list` fields.
 *   min/max — numeric bounds for `number` fields.
 *   suffix  — unit rendered inside a `number` field (e.g. "%").
 *   wide    — span the full form width; implied for `textarea` and `list`.
 *   rowStart— begin a new grid row, so this field (and the ones after it, until
 *             the row fills) is not left dangling on the end of the previous row.
 */

// Option sets for the dropdown fields (mirror the LOS enums).
const CONSTITUTION_OPTIONS = [
    "Proprietorship",
    "Partnership",
    "Private Limited",
    "Public Limited",
    "Huf",
    "LLP"
];
const BUSINESS_NATURE_OPTIONS = [
    "Retail Business",
    "Wholesale Business",
    "Factory/Manufacturing",
    "Supplier/Services",
    "Office/Sales Office",
    "Warehouse/Depot",
    "Bonded/Warehouse",
    "Import",
    "Export",
    "Works Contract",
    "Others"
];
const BUSINESS_PREMISES_OPTIONS = ["Owned", "Rented", "Leased"];
const RESIDENTIAL_PREMISES_OPTIONS = ["Owned", "Rented", "Leased", "Shared"];
const YES_NO_OPTIONS = ["No", "Yes"];

// No repeating groups remain in the form. Promoters / Family profile / Group
// firms / GST FY-wise / ECR trends / Live loans were removed or downgraded to
// free text, and Brand mix is now the shared BrandMixCard (rendered by the
// section, not declared as a field), so the `list` field type has no consumers.

export const TVR_SUBSECTIONS = [
    {
        id: "tvr_source",
        title: "TVR Source",
        icon: "phone",
        fields: [
            {
                key: "tvr_conducted_with",
                path: "tvr_conducted_with",
                label: "TVR conducted with",
                // Pick an applicant from the dropdown OR type someone else in.
                // The options are the case's APPLICANTS — the same pool the call
                // picker offers — so they are supplied at render time by
                // updated-tvr-section, not hard-coded here.
                type: "combo",
                optionsFrom: "applicants",
                suggestable: true
            }
        ]
    },
    {
        id: "business_overview",
        title: "Business Overview",
        icon: "shop",
        fields: [
            // Non-date fields first…
            {
                key: "constitution",
                path: "business_overview.constitution",
                label: "Constitution",
                type: "select",
                options: CONSTITUTION_OPTIONS
            },
            {
                key: "nature_of_business",
                path: "business_overview.nature_of_business",
                label: "Nature of business",
                type: "select",
                options: BUSINESS_NATURE_OPTIONS,
                suggestable: true
            },
            {
                key: "business_premises",
                path: "business_overview.business_premises",
                label: "Business premises",
                type: "select",
                options: BUSINESS_PREMISES_OPTIONS,
                suggestable: true
            },
            {
                key: "residential_premises",
                path: "business_overview.residential_premises",
                label: "Residential premises",
                type: "select",
                options: RESIDENTIAL_PREMISES_OPTIONS,
                suggestable: true
            },
            {
                key: "ownership_details",
                path: "business_overview.ownership_details",
                label: "Ownership Details",
                type: "text",
                suggestable: true
            },
            // …then every date together on its own line. `rowStart` forces a grid
            // row break, so the three sit on one line regardless of how the fields
            // above happen to wrap.
            {
                key: "incorporation_date",
                path: "business_overview.incorporation_date",
                label: "Inception — incorporation date",
                type: "date",
                rowStart: true
            },
            {
                key: "gst_registration_date",
                path: "business_overview.gst_registration_date",
                label: "Inception — GST registration date",
                type: "date"
            },
            {
                key: "anchor_associated_since",
                path: "business_overview.anchor_associated_since",
                label: "Anchor association date",
                type: "date"
            },
            // Wide fields last — each spans the full form width.
            // A one-line brand-mix digest (top-5 manufacturers by supply-chain
            // value), pre-filled by the backend from the same GST pull the Company
            // Detail Brand Mix table shows. The full interactive table lives in
            // Company Detail; this is just the TVR's text summary of it.
            {
                key: "brand_mix_summary",
                path: "business_overview.brand_mix_summary",
                label: "Brand/Seller mix (top manufacturers by value)",
                type: "textarea"
            },
            {
                key: "promoter_background",
                path: "business_overview.promoter_background",
                label: "Promoter background",
                type: "textarea",
                suggestable: true
            },
            {
                key: "other_business_details",
                path: "business_overview.other_business_details",
                label: "Other business details",
                type: "textarea",
                suggestable: true
            },
            {
                key: "family_profile",
                path: "business_overview.family_profile",
                label: "Family profile",
                type: "textarea",
                suggestable: true
            },
            {
                key: "group_firms",
                path: "business_overview.group_firm_details",
                label: "Group firm details (if any)",
                type: "textarea",
                suggestable: true
            }
        ]
    },
    // Financial Performance, Banking & Credit and Additional Information were all
    // collapsed to free-text remarks: the structured fields (GST turnover, ECR
    // trends, banking-facility limits, RM details, eligibility…) are captured
    // elsewhere in the case, so restating them here was duplicate data entry.
    // `remark` / `risk` are NEW nested keys — the API validates top-level sections
    // only, so nested additions should pass, but confirm against the serializer.
    {
        id: "financial_performance",
        title: "Financial Performance",
        icon: "line-chart",
        // The footer (the read-only Limit Assessment block) renders ABOVE the
        // fields here, so the editable Remark sits below the headline numbers.
        footerFirst: true,
        fields: [
            {
                key: "financial_remark",
                path: "financial_performance.remark",
                label: "Remark",
                type: "textarea",
                suggestable: true
            }
        ]
    },
    {
        id: "banking_credit",
        title: "Banking & CIBIL Profile",
        icon: "credit-card",
        fields: [
            {
                key: "cibil_enquiries",
                path: "banking_credit_profile.cibil_enquiries_last_3_months",
                label: "CIBIL Remarks",
                type: "textarea"
            },
            {
                key: "banking_remark",
                path: "banking_credit_profile.remark",
                label: "Banking Remarks",
                type: "textarea"
            }
        ]
    },
    {
        id: "additional_info",
        title: "Additional Information",
        icon: "info-circle",
        fields: [
            {
                key: "buyer_seller_relation",
                path: "additional_information.buyer_seller_relation",
                label: "Buyer / Seller relation in secondary program",
                // Full-width so the long label fits on one line.
                wide: true,
                // A plain string ("Yes" / "No"), not a boolean: the boolean
                // control is tri-state (null = unanswered), which this field does
                // not need. The backend seeds "No" for a fresh case.
                type: "select",
                options: YES_NO_OPTIONS,
                suggestable: true
            },
            {
                key: "risk",
                path: "additional_information.risk",
                label: "Risk",
                type: "textarea",
                suggestable: true
            }
        ]
    }
];

// Flat list of every field, in declaration order.
export const TVR_FIELDS = TVR_SUBSECTIONS.reduce(
    (acc, section) => acc.concat(section.fields),
    []
);

export const TVR_FIELD_KEYS = TVR_FIELDS.map(f => f.key);

/**
 * Allowed values for AI suggestions on closed-list TVR fields.
 * Must stay in sync with Chitthi ``apps/telephony/services/tvr_suggestions.py``
 * (``TVR_FIELD_ENUM_OPTIONS``).
 *
 * ``tvr_conducted_with`` is NOT here — options come from case applicants at runtime.
 */
export const TVR_SUGGESTION_ENUM_OPTIONS = {
    "business_overview.nature_of_business": BUSINESS_NATURE_OPTIONS,
    "business_overview.business_premises": BUSINESS_PREMISES_OPTIONS,
    "business_overview.residential_premises": RESIDENTIAL_PREMISES_OPTIONS,
    "additional_information.buyer_seller_relation": YES_NO_OPTIONS
};

/**
 * Accept fills the form only (not persisted until Submit).
 * Dropdown/combo: replace the control value.
 * Free text / textarea: append after existing text with a blank line.
 */
export const TVR_SUGGESTION_REPLACE_ON_ACCEPT_PATHS = new Set([
    "tvr_conducted_with",
    ...Object.keys(TVR_SUGGESTION_ENUM_OPTIONS)
]);

export function shouldAppendSuggestionOnAccept(field) {
    if (!field || !field.suggestable) {
        return false;
    }
    if (field.type === "select" || field.type === "combo") {
        return false;
    }
    if (TVR_SUGGESTION_REPLACE_ON_ACCEPT_PATHS.has(field.path)) {
        return false;
    }
    return field.type === "text" || field.type === "textarea";
}

export function draftValueFromSuggestion(field, currentValue, suggestionText) {
    const sug = (suggestionText != null ? String(suggestionText) : "").trim();
    if (!sug) {
        return currentValue;
    }
    if (!shouldAppendSuggestionOnAccept(field)) {
        return sug;
    }
    const prior = (currentValue != null ? String(currentValue) : "").trim();
    if (!prior) {
        return sug;
    }
    return `${prior}\n\n${sug}`;
}
